import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Search, Instagram, Youtube, IndianRupee, Award, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { ApifyRefreshButton } from '../../components/ApifyRefreshButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

export const AdminRoster = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'account_executive';
  const [creators, setCreators] = useState([]);
  const [talentManagers, setTalentManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [exclusivityFilter, setExclusivityFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [assigningId, setAssigningId] = useState(null);

  const fetchCreators = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/creators`, { withCredentials: true });
      setCreators(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchTalentManagers = async () => {
    // Admin-only endpoint — AE skips this fetch.
    if (isReadOnly) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/assignments/talent-managers`, { withCredentials: true });
      setTalentManagers(data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchCreators();
    fetchTalentManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignTM = async (creatorId, tmId) => {
    setAssigningId(creatorId);
    try {
      if (!tmId || tmId === 'none') {
        await axios.delete(`${API_URL}/api/admin/assignments/creators/${creatorId}/assign`, { withCredentials: true });
        toast.success('Unassigned');
      } else {
        await axios.post(`${API_URL}/api/admin/assignments/creators/${creatorId}/assign`,
          { talent_manager_id: tmId }, { withCredentials: true });
        const tm = talentManagers.find((t) => t.id === tmId);
        toast.success(`Assigned to ${tm?.name || 'Talent Manager'}`);
      }
      await Promise.all([fetchCreators(), fetchTalentManagers()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigningId(null);
    }
  };

  const filtered = creators.filter((c) => {
    if (q && !`${c.name} ${c.email} ${c.instagram_handle || ''}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (exclusivityFilter !== 'all' && c.exclusivity_type !== exclusivityFilter) return false;
    if (gradeFilter !== 'all' && c.quality_grade !== gradeFilter) return false;
    return true;
  });

  const exclusiveCount = creators.filter((c) => c.exclusivity_type === 'exclusive').length;
  const marketplaceCount = creators.filter((c) => c.exclusivity_type === 'marketplace').length;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="admin-roster-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <Users className="text-[#FF451A]" size={32} /> Creator Roster
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">All creators registered on Vyvre.</p>
            </div>
            {user?.role === 'admin' && (
              <ApifyRefreshButton
                allMissing
                label="Refresh Missing Data"
                className="border border-[#FF451A] text-[#FF451A] hover:bg-[#FF451A]/10"
                testid="roster-refresh-missing-btn"
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
              <div className="text-xs uppercase text-[#6B6B6B] dark:text-zinc-500 tracking-wider mb-1">Total Creators</div>
              <div className="text-3xl font-bold text-[#0F0F0F] dark:text-white">{creators.length}</div>
            </div>
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
              <div className="text-xs uppercase text-[#6B6B6B] dark:text-zinc-500 tracking-wider mb-1 flex items-center gap-1"><Award size={11} /> Exclusive</div>
              <div className="text-3xl font-bold text-[#0F0F0F] dark:text-white">{exclusiveCount}</div>
            </div>
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
              <div className="text-xs uppercase text-[#6B6B6B] dark:text-zinc-500 tracking-wider mb-1">Marketplace</div>
              <div className="text-3xl font-bold text-[#0F0F0F] dark:text-white">{marketplaceCount}</div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] dark:text-zinc-500" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email, handle..."
                  className="pl-9 rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="roster-search-input"
                />
              </div>
              <Select value={exclusivityFilter} onValueChange={setExclusivityFilter}>
                <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="exclusive">Exclusive</SelectItem>
                  <SelectItem value="marketplace">Marketplace</SelectItem>
                </SelectContent>
              </Select>
              <Select value={gradeFilter} onValueChange={setGradeFilter}>
                <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  <SelectItem value="A+">Grade A+</SelectItem>
                  <SelectItem value="A">Grade A</SelectItem>
                  <SelectItem value="B">Grade B</SelectItem>
                  <SelectItem value="C">Grade C</SelectItem>
                  <SelectItem value="D">Grade D</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white dark:bg-[#0A0B0D] text-[#6B6B6B] dark:text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-4 font-semibold">Creator</th>
                    <th className="text-left p-4 font-semibold">Type</th>
                    <th className="text-left p-4 font-semibold">IG / YT</th>
                    <th className="text-left p-4 font-semibold">Engagement</th>
                    <th className="text-left p-4 font-semibold">Reel Price</th>
                    <th className="text-left p-4 font-semibold">Grade</th>
                    <th className="text-left p-4 font-semibold">Categories</th>
                    <th className="text-left p-4 font-semibold">Talent Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} className="p-12 text-center text-[#6B6B6B] dark:text-zinc-500">No creators match your filters</td></tr>
                  ) : filtered.map((c) => (
                    <tr key={c.id} className="border-t border-black/[0.08] dark:border-white/5 hover:bg-white/[0.03]" data-testid="roster-row">
                      <td className="p-4">
                        <Link to={`/creators/${c.id}`} className="flex items-center gap-3 group">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white font-bold shrink-0">
                            {c.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-[#0F0F0F] dark:text-white group-hover:text-[#FF451A] transition-colors">{c.name}</div>
                            <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">@{c.instagram_handle || c.email}</div>
                          </div>
                        </Link>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold border ${
                            c.exclusivity_type === 'exclusive'
                              ? 'bg-[#FF451A]/10 text-[#FF451A] border-[#FF451A]/25'
                              : 'bg-zinc-100 dark:bg-[#131518] text-[#6B6B6B] dark:text-zinc-400 border-black/[0.06] dark:border-white/10'
                          }`}
                        >
                          {c.exclusivity_type === 'exclusive' ? 'Exclusive' : 'Marketplace'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-xs">
                          <Instagram size={11} className="text-[#E1306C]" /> {fmtNum(c.instagram_followers || 0)}
                        </div>
                        <div className="flex items-center gap-1 text-xs mt-1">
                          <Youtube size={11} className="text-[#FF0000]" /> {fmtNum(c.youtube_subscribers || 0)}
                        </div>
                      </td>
                      <td className="p-4">{(c.instagram_engagement || 0).toFixed(1)}%</td>
                      <td className="p-4 flex items-center gap-0.5"><IndianRupee size={12} />{(c.pricing_reel || 0).toLocaleString()}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full bg-[#FFB800]/10 text-[#B8860B] dark:text-[#FFB800] border border-[#FFB800]/30 text-xs font-semibold">
                          {c.quality_grade || '—'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(c.categories || []).slice(0, 2).map((cat) => (
                            <span key={cat} className="px-2 py-0.5 text-xs rounded-full bg-[#FF451A]/10 text-[#FF451A]">{cat}</span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 min-w-[200px]">
                        {isReadOnly ? (
                          <span
                            className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-zinc-100 dark:bg-[#131518] border border-black/[0.10] dark:border-white/10 text-xs text-[#6B6B6B] dark:text-zinc-400"
                            data-testid={`assign-tm-readonly-${c.id}`}
                          >
                            <UserCheck size={12} className={c.assigned_talent_manager_id ? 'text-[#00C972]' : 'text-[#6B6B6B] dark:text-zinc-500'} />
                            {c.assigned_talent_manager_id ? 'Assigned' : 'Unassigned'}
                          </span>
                        ) : (
                          <Select
                            value={c.assigned_talent_manager_id || 'none'}
                            onValueChange={(v) => assignTM(c.id, v)}
                            disabled={assigningId === c.id}
                          >
                            <SelectTrigger className="rounded-lg text-xs h-9 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid={`assign-tm-${c.id}`}>
                              <div className="flex items-center gap-2 truncate">
                                <UserCheck size={12} className={c.assigned_talent_manager_id ? 'text-[#00C972]' : 'text-[#6B6B6B] dark:text-zinc-500'} />
                                <SelectValue placeholder="Unassigned" />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Unassigned</SelectItem>
                              {talentManagers.map((tm) => (
                                <SelectItem key={tm.id} value={tm.id}>
                                  {tm.name} <span className="text-[#6B6B6B] dark:text-zinc-500">({tm.roster_count})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
